import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Users } from 'lucide-react'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { FilterField } from '@/components/FilterButton'
import { StatusFilterButton, type StatusFilterValue } from '@/components/StatusFilter'
import { AdvertiserSelect } from '@/components/AdvertiserSelect'
import { SearchSelect } from '@/components/SearchSelect'
import { UserSelect } from '@/components/UserSelect'
import { useNetwork } from '@/contexts/NetworkContext'
import {
  ORDER_DELIVERY_STATES,
  type OrderDeliveryState,
  type OrderFetchFilters,
} from '@/services/orders/ordersApi'
import { TeamsApi } from '@/services/users/teamsApi'
import { UsersApi } from '@/services/users/usersApi'

export interface OrderFilterState {
  objectState: StatusFilterValue
  deliveryState: OrderDeliveryState | 'all'
  /** Advertiser id, or '' for every advertiser. */
  advertiserId: string
  /** Team id, or '' for every team. */
  teamId: string
  /** The picked team's name — what the API matches on. */
  teamName: string
  /** Salesperson's user id, or '' for anyone. */
  salespersonId: string
  /** Ad ops user id, or '' for anyone. */
  adOpsId: string
}

export const DEFAULT_ORDER_FILTERS: OrderFilterState = {
  objectState: 'ACTIVE',
  deliveryState: 'all',
  advertiserId: '',
  teamId: '',
  teamName: '',
  salespersonId: '',
  adOpsId: '',
}

/** The filters as the order API takes them. */
export function orderFetchFilters(filters: OrderFilterState): OrderFetchFilters {
  return {
    deliveryState: filters.deliveryState === 'all' ? undefined : filters.deliveryState,
    advertiserId: filters.advertiserId,
    teamName: filters.teamId ? filters.teamName : '',
    salespersonId: filters.salespersonId,
    adOpsId: filters.adOpsId,
  }
}

interface OrderFiltersProps {
  filters: OrderFilterState
  onChange: (filters: OrderFilterState) => void
  /** What this list treats as unfiltered. */
  defaults?: OrderFilterState
}

/** Status, delivery, advertiser, team and people filters for an order list, in one button. */
export function OrderFilters({
  filters,
  onChange,
  defaults = DEFAULT_ORDER_FILTERS,
}: OrderFiltersProps) {
  const { t } = useTranslation()
  const { scopeKey } = useNetwork()
  const [usersEnabled, setUsersEnabled] = useState(!!filters.salespersonId || !!filters.adOpsId)

  const { data: teams = [] } = useQuery({
    queryKey: ['teams', 'listFilter', scopeKey],
    queryFn: () => TeamsApi.getAll(),
    staleTime: 5 * 60 * 1000,
  })
  const teamOptions = useMemo(
    () => teams.map((team) => ({ value: team.id, label: team.name })),
    [teams]
  )
  // A single team has nothing to narrow, but a remembered pick stays visible
  // so it can be cleared.
  const teamVisible = teams.length > 1 || !!filters.teamId

  // The same user list the order editor's people pickers read.
  const { data: usersData, isFetching: usersFetching } = useQuery({
    queryKey: ['order-users'],
    queryFn: () =>
      UsersApi.getAll({
        includeHidden: false,
        includeActive: true,
        includeInactive: false,
        minimal: true,
        orderBy: 'name',
        pageSize: 1001,
      }),
    enabled: usersEnabled,
    staleTime: Infinity,
  })
  const users = usersData?.results ?? []

  const label = (value: OrderFilterState['deliveryState']) =>
    value === 'all' ? t('common.all') : t(`deliveryStatuses.${value}`)

  type IdField = 'advertiserId' | 'teamId' | 'salespersonId' | 'adOpsId'
  const changed = (key: IdField, visible = true) =>
    visible && filters[key] !== defaults[key] ? 1 : 0

  return (
    <StatusFilterButton
      value={filters.objectState}
      onChange={(objectState) => onChange({ ...filters, objectState })}
      defaultValue={defaults.objectState}
      extraActiveCount={
        (filters.deliveryState !== defaults.deliveryState ? 1 : 0) +
        changed('advertiserId') +
        changed('teamId', teamVisible) +
        changed('salespersonId') +
        changed('adOpsId')
      }
      onReset={() => onChange(defaults)}
    >
      <FilterField label={t('common.delivery')}>
        <Select
          value={filters.deliveryState}
          onValueChange={(v) =>
            onChange({ ...filters, deliveryState: v as OrderFilterState['deliveryState'] })
          }
        >
          <SelectTrigger className="h-8 w-full text-sm">
            <SelectValue>{label(filters.deliveryState)}</SelectValue>
          </SelectTrigger>
          <SelectContent>
            {(['all', ...ORDER_DELIVERY_STATES] as const).map((value) => (
              <SelectItem key={value} value={value}>
                {label(value)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </FilterField>

      <FilterField label={t('common.advertiser')}>
        <AdvertiserSelect
          value={filters.advertiserId}
          onChange={(advertiserId) => onChange({ ...filters, advertiserId })}
          anyLabel={t('common.all')}
        />
      </FilterField>

      {teamVisible && (
        <FilterField label={t('common.team')}>
          <SearchSelect
            value={filters.teamId}
            onChange={(teamId) =>
              onChange({
                ...filters,
                teamId,
                teamName: teams.find((team) => team.id === teamId)?.name ?? '',
              })
            }
            options={teamOptions}
            placeholder={t('common.all')}
            clearable
            icon={Users}
            searchPlaceholder={t('teams.searchTeams')}
            emptyText={t('teams.noTeamsFound')}
            className="h-8 text-sm"
          />
        </FilterField>
      )}

      {/* The API can match one person per list, so picking one clears the other. */}
      <FilterField label={t('orders.salespersonUser')}>
        <UserSelect
          value={filters.salespersonId}
          onChange={(salespersonId) =>
            onChange({ ...filters, salespersonId, adOpsId: salespersonId ? '' : filters.adOpsId })
          }
          users={users}
          loading={usersFetching}
          onOpen={() => setUsersEnabled(true)}
          placeholder={t('common.all')}
          className="h-8 text-sm"
        />
      </FilterField>

      <FilterField label={t('orders.adOpsUser')}>
        <UserSelect
          value={filters.adOpsId}
          onChange={(adOpsId) =>
            onChange({ ...filters, adOpsId, salespersonId: adOpsId ? '' : filters.salespersonId })
          }
          users={users}
          loading={usersFetching}
          onOpen={() => setUsersEnabled(true)}
          placeholder={t('common.all')}
          className="h-8 text-sm"
        />
      </FilterField>
    </StatusFilterButton>
  )
}
